import { useId, useState, type ReactNode } from 'react';
import { uuidv7 } from 'uuidv7';
import type { AnalysisItem } from '@/domain/schema/v1';
import type { SaveStatus } from '../hooks/useAutosave';

export function SaveIndicator({
  status,
  error,
  onRetry,
}: {
  status: SaveStatus;
  error?: string | null;
  onRetry?: () => void;
}) {
  const text: Record<SaveStatus, string> = {
    idle: '',
    pending: '입력 중…',
    saving: '저장 중…',
    saved: '저장됨',
    error: '저장 실패',
    conflict: '다른 곳에서 먼저 수정됨',
  };
  if (status === 'idle') return null;
  return (
    <span role="status" className={`save-indicator ${status}`} title={error ?? undefined}>
      {text[status]}
      {status === 'error' && onRetry && (
        <button type="button" className="link" onClick={onRetry}>
          다시 시도
        </button>
      )}
    </span>
  );
}

export function ConflictBanner(props: { onMine: () => void; onTheirs: () => void }) {
  return (
    <div role="alert" className="banner warn-banner">
      다른 탭이나 기기에서 이 기록이 먼저 바뀌었습니다. 입력하신 내용은 아직 화면에 있습니다.
      <div className="row">
        <button type="button" onClick={props.onMine}>
          내 입력으로 저장 (새 버전)
        </button>
        <button type="button" onClick={props.onTheirs}>
          최신 내용 불러오기 (내 입력 버림)
        </button>
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  badge,
  children,
}: {
  label: string;
  hint?: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <div className="field-label">
        <span>{label}</span>
        {badge}
      </div>
      {children}
      {hint && <p className="muted small">{hint}</p>}
    </div>
  );
}

export function TextInput(props: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number';
  readOnly?: boolean;
  hint?: string;
  badge?: ReactNode;
}) {
  const id = useId();
  return (
    <Field label={props.label} {...(props.hint ? { hint: props.hint } : {})} badge={props.badge}>
      <input
        id={id}
        aria-label={props.label}
        type={props.type ?? 'text'}
        value={props.value ?? ''}
        readOnly={props.readOnly}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextArea(props: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  readOnly?: boolean;
  rows?: number;
  hint?: string;
  badge?: ReactNode;
  placeholder?: string;
}) {
  return (
    <Field label={props.label} {...(props.hint ? { hint: props.hint } : {})} badge={props.badge}>
      <textarea
        aria-label={props.label}
        value={props.value ?? ''}
        readOnly={props.readOnly}
        rows={props.rows ?? 4}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Field>
  );
}

/** string[]을 한 줄에 하나씩 편집 */
export function LinesInput(props: {
  label: string;
  value: string[] | undefined;
  onChange: (v: string[]) => void;
  readOnly?: boolean;
  hint?: string;
}) {
  const [text, setText] = useState((props.value ?? []).join('\n'));
  return (
    <TextArea
      label={props.label}
      value={text}
      readOnly={props.readOnly}
      rows={Math.max(2, Math.min(8, (props.value?.length ?? 0) + 1))}
      hint={props.hint ?? '한 줄에 하나씩'}
      onChange={(t) => {
        setText(t);
        props.onChange(
          t
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean),
        );
      }}
    />
  );
}

export function Select<T extends string>(props: {
  label: string;
  value: T | undefined;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <Field label={props.label}>
      <select
        aria-label={props.label}
        value={props.value ?? ''}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value as T)}
      >
        {props.value === undefined && <option value="">선택</option>}
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

const ITEM_STATUS = [
  { value: 'hypothesis', label: '가설' },
  { value: 'observation', label: '관찰' },
  { value: 'confirmed', label: '확인됨' },
] as const;

/**
 * AnalysisItem 목록 편집. 각 항목의 id를 유지해 revision 간 같은 가설을 추적한다.
 * 문장은 줄바꿈되어 전체가 보이고, 상태(관찰·가설·확인됨)는 한 번 눌러 고른다. 지운 항목은 바로 되돌릴 수 있다.
 */
export function ItemsEditor(props: {
  label: string;
  value: AnalysisItem[] | undefined;
  onChange: (v: AnalysisItem[]) => void;
  readOnly?: boolean;
  /** 칸 이름·안내 없이 (바깥에서 제목을 보여줄 때) */
  bare?: boolean;
}) {
  const items = props.value ?? [];
  const [removed, setRemoved] = useState<{ item: AnalysisItem; index: number } | null>(null);
  const update = (i: number, patch: Partial<AnalysisItem>) =>
    props.onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <Bare bare={props.bare} label={props.label} hint="가설은 사실이 아닙니다. 확인된 경우에만 '확인됨'으로 바꾸세요.">
      <ul className="items">
        {items.map((it, i) => (
          <li key={it.id} className="item-row">
            <textarea
              className="item-text"
              aria-label={`${props.label} ${i + 1}`}
              rows={1}
              value={it.text}
              readOnly={props.readOnly}
              onChange={(e) => update(i, { text: e.target.value })}
            />
            <div className="item-side">
              <div className="seg-group" role="group" aria-label={`${props.label} ${i + 1} 상태`}>
                {ITEM_STATUS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={it.status === o.value ? 'seg-btn on' : 'seg-btn'}
                    aria-pressed={it.status === o.value}
                    disabled={props.readOnly}
                    onClick={() => update(i, { status: o.value })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {!props.readOnly && (
                <button
                  type="button"
                  className="item-del"
                  aria-label="삭제"
                  onClick={() => {
                    setRemoved({ item: it, index: i });
                    props.onChange(items.filter((_, j) => j !== i));
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {removed && (
        <p className="small undo-line" role="status">
          지움: {removed.item.text.length > 40 ? `${removed.item.text.slice(0, 40)}…` : removed.item.text}{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              const next = [...items];
              next.splice(Math.min(removed.index, next.length), 0, removed.item);
              props.onChange(next);
              setRemoved(null);
            }}
          >
            되돌리기
          </button>
        </p>
      )}
      {!props.readOnly && (
        <button
          type="button"
          onClick={() => props.onChange([...items, { id: uuidv7(), text: '', status: 'hypothesis' }])}
        >
          + 항목 추가
        </button>
      )}
    </Bare>
  );
}

export function SourceBadge({ source }: { source: string | undefined }) {
  if (!source) return null;
  const map: Record<string, [string, string]> = {
    auto: ['자동 채움', 'badge-auto'],
    'ai-draft': ['AI 초안', 'badge-auto'],
    user: ['상담자 작성', 'badge-user'],
    'needs-review': ['확인 필요', 'badge-review'],
    missing: ['자료 없음', 'badge-missing'],
  };
  const [text, cls] = map[source] ?? [source, ''];
  return <span className={`badge ${cls}`}>{text}</span>;
}

export function ConfirmButton(props: {
  label: string;
  confirmText: string;
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed)
    return (
      <button type="button" className={props.className} disabled={props.disabled} onClick={() => setArmed(true)}>
        {props.label}
      </button>
    );
  return (
    <div className="confirm">
      <p className="small">{props.confirmText}</p>
      <div className="row">
        <button
          type="button"
          className="primary"
          onClick={() => {
            setArmed(false);
            props.onConfirm();
          }}
        >
          확인
        </button>
        <button type="button" onClick={() => setArmed(false)}>
          취소
        </button>
      </div>
    </div>
  );
}

/** bare면 묶음 없이 내용만, 아니면 칸 이름·안내가 있는 Field */
function Bare(props: { bare?: boolean | undefined; label: string; hint: string; children: ReactNode }) {
  if (props.bare) return <div className="field bare-field">{props.children}</div>;
  return (
    <Field label={props.label} hint={props.hint}>
      {props.children}
    </Field>
  );
}

/** 문장 목록 편집 (요인 칸 등): 한 줄에 한 항목, 전체 문장이 보이고, 지우면 되돌릴 수 있다 */
export function StringListEditor(props: {
  label: string;
  value: string[] | undefined;
  onChange: (v: string[]) => void;
  readOnly?: boolean;
}) {
  const items = props.value ?? [];
  const [removed, setRemoved] = useState<{ text: string; index: number } | null>(null);
  return (
    <div className="field bare-field">
      <ul className="items">
        {items.map((t, i) => (
          <li key={i} className="item-row">
            <textarea
              className="item-text"
              aria-label={`${props.label} ${i + 1}`}
              rows={1}
              value={t}
              readOnly={props.readOnly}
              onChange={(e) => props.onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
            />
            {!props.readOnly && (
              <div className="item-side">
                <button
                  type="button"
                  className="item-del"
                  aria-label="삭제"
                  onClick={() => {
                    setRemoved({ text: t, index: i });
                    props.onChange(items.filter((_, j) => j !== i));
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {removed && (
        <p className="small undo-line" role="status">
          지움: {removed.text.length > 40 ? `${removed.text.slice(0, 40)}…` : removed.text}{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              const next = [...items];
              next.splice(Math.min(removed.index, next.length), 0, removed.text);
              props.onChange(next);
              setRemoved(null);
            }}
          >
            되돌리기
          </button>
        </p>
      )}
      {!props.readOnly && (
        <button type="button" onClick={() => props.onChange([...items, ''])}>
          + 항목 추가
        </button>
      )}
    </div>
  );
}
